import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useGetFilePaths } from '../../../../redux/api/appConfigApi';
import {
  useGetMyDetails,
  useUpdateUser,
} from '../../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../../utils/files/firebase-storage-utils';
import { getUserInfoFormValidation } from './FormikData';

export function useUserInfoEditComponent() {
  const { data: user } = useGetMyDetails();
  const [updateUser] = useUpdateUser();
  const { t } = useTranslation();
  const [avatar, setAvatar] = useState(null);
  const { replace } = useNavigationStack();
  const { data: filePaths } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getUserInfoFormValidation(t),
    initialValues: {
      firstname: user.firstname,
      lastname: user.lastname,
      description: user.description,
      avatar: '',
    },
    onSubmit,
  });

  async function onSubmit() {
    if (!filePaths || !user || !user.uid || !user.id) {
      return;
    }

    let avatarFile;
    try {
      setIsLoading(true);

      if (avatar) {
        avatarFile = await uploadFileAndCreateAppFile(
          avatar,
          filePaths.users,
          user.uid,
          user.id,
          FileVisibility.public,
        );
      }

      await updateUser({
        id: user.id,
        body: {
          ...formik.values,
          avatar: avatarFile
            ? {
                id: avatarFile.id,
                name: avatarFile.name,
                mimeType: avatarFile.mimeType,
                size: avatarFile.size,
                storageLink: avatarFile.storageLink,
                previewStorageLink: avatarFile.previewStorageLink,
              }
            : undefined,
        },
      }).unwrap();

      replace(Paths.settings.toInfoView);
    } catch (error) {
      console.error('Error uploading avatar or updating user profile:', error);
      errorToastMessage(t('messages.uploadOrSaveFailed'));
      if (avatarFile) {
        await deleteFilesFromFirebase(
          [avatarFile?.storageLink, avatarFile?.previewStorageLink].filter(
            Boolean,
          ),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    user,
    formik,
    avatar,
    setAvatar,
    isLoading,
  };
}
